/* Farpoint -- add-on preview ("(i)") for product options
 * ===================================================
 *
 * THE PROBLEM
 * An option value like "Soft Ground Foot, set of three (3) for Wheeley Bars
 * with 5/8" Leveling Screws" is a real catalogue product, but on the parent
 * product page the customer can only tick it. To find out what it actually is
 * they have to leave the page and go hunting in the catalogue -- so most don't,
 * and the add-on doesn't sell.
 *
 * WHAT THIS DOES
 * Puts a small (i) after each variant-backed option value. Clicking it fetches
 * that product's real data from `/products/<handle>.js` and shows it in a
 * panel: image, title, description, and a link to the full listing. Price is
 * deliberately omitted -- see render().
 * Nothing is fetched until the customer asks, so the product page carries no
 * extra weight.
 *
 * WHY IT INJECTS INSTEAD OF BEING RENDERED IN LIQUID
 * The compiled spec is already in the DOM at `[data-fp-spec]`, and every option
 * value already maps to a stable pair of selectors -- `[data-fp-option="<key>"]`
 * plus an input whose value is the value key. That is exactly how
 * fp-options.js finds things, so this reuses a mapping that is already load
 * bearing rather than adding markup to a 13 KB snippet. It is purely additive:
 * if anything fails to match, no (i) appears and the options behave as before.
 *
 * DEPENDS ON `handle`
 * The (i) only renders for values whose compiled spec entry carries a `handle`.
 * That field is written by App Home's Publish (`lib.js` -> `compile()`). Until a
 * set is republished, its values have no handle and no (i) appears -- which is the
 * intended degradation, not a bug.
 *
 * ASCII ONLY, DELIBERATELY. This file is hand-transported into the theme and
 * verified by md5; multi-byte characters in comments caused repeated few-byte
 * drift that cost more to diagnose than the typography was worth.
 */
(function () {
  "use strict";

  window.FP_OPTION_PREVIEW_VERSION = "2026-08-01.5";

  var CACHE = {};
  var panel = null;
  var lastFocus = null;

  ready(init);

  function init() {
    var specEl = document.querySelector("[data-fp-spec]");
    var root = document.querySelector("[data-fp-options]");
    if (!specEl || !root) return;

    var spec = parse(specEl);
    if (!spec || !Array.isArray(spec.options)) return;

    injectStyles();

    for (var o = 0; o < spec.options.length; o++) {
      var opt = spec.options[o];
      if (!Array.isArray(opt.values)) continue;

      var group = root.querySelector('[data-fp-option="' + cssEscape(opt.key) + '"]');
      if (!group) continue;

      // WHERE THE PILL GOES
      // Preferred: the group's grey header bar, right-justified -- it reads as
      // "more about this option" and does not push the value label around.
      //
      // That only works when the group has exactly ONE product behind it. A
      // radio or checkbox group with several variant-backed values has several
      // different products, and a single header pill could only point at one of
      // them. Those fall back to a pill per value, which is the honest layout.
      var withHandle = [];
      for (var h = 0; h < opt.values.length; h++) {
        if (opt.values[h] && opt.values[h].handle) withHandle.push(opt.values[h]);
      }

      if (withHandle.length === 1) {
        attachToHeader(group, withHandle[0]);
      } else {
        for (var i = 0; i < opt.values.length; i++) {
          attach(group, opt.values[i]);
        }
      }
    }

    // Delegated: one listener regardless of how many buttons exist.
    document.addEventListener("click", function (e) {
      var btn = e.target && e.target.closest && e.target.closest("[data-fp-preview]");
      if (!btn) return;
      // The button sits inside the option's <label>, so without both of these a
      // click would also toggle the checkbox it is describing.
      e.preventDefault();
      e.stopPropagation();
      open(btn.getAttribute("data-fp-preview"), btn.getAttribute("data-fp-preview-label"));
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") close();
    });
  }

  function attach(group, value) {
    if (!value || !value.handle) return; // nothing to preview

    // Dropdowns render values as <option>, which cannot hold a button. Those
    // values are skipped; in practice dropdown values on this store are
    // price-only (no variant), so there is nothing to preview anyway.
    var input = group.querySelector('input[value="' + cssEscape(value.key) + '"]');
    var host = input && input.parentNode && input.parentNode.querySelector("span");
    if (!host) return;
    if (host.querySelector("[data-fp-preview]")) return; // already attached

    host.appendChild(document.createTextNode(" "));
    host.appendChild(makeBtn(value));
  }

  // A labelled pill, not a bare glyph. The first pass used a small (i) and it
  // read as decoration -- customers did not know it was clickable, which is the
  // entire point. Spelling out the action is worth the width.
  function makeBtn(value) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "fp-info";
    btn.setAttribute("data-fp-preview", value.handle);
    btn.setAttribute("data-fp-preview-label", value.label || "");
    btn.setAttribute("aria-label", "About " + (value.label || "this add-on"));
    btn.title = "About " + (value.label || "this add-on");
    btn.textContent = "More Information";
    return btn;
  }

  // Header placement. The header is a <label for="fp-...">, so a click inside it
  // would normally activate that option's input -- the delegated handler's
  // preventDefault/stopPropagation is what stops that, exactly as for the
  // inline case.
  function attachToHeader(group, value) {
    var label = group.querySelector(".fp-option__label");
    if (!label) return attach(group, value); // unknown markup: fall back inline
    if (label.querySelector("[data-fp-preview]")) return;

    // Scoped class so only headers that actually carry a pill become flex
    // containers; every other option label keeps the theme's own layout.
    label.classList.add("fp-has-info");
    label.appendChild(makeBtn(value));
  }

  /* ---------- panel ---------- */

  function open(handle, label) {
    lastFocus = document.activeElement;
    build();
    panel.classList.add("is-open");
    document.documentElement.style.overflow = "hidden";
    setBody('<div class="fp-preview__loading">Loading...</div>', label || "");

    load(handle)
      .then(function (p) {
        render(p);
      })
      .catch(function () {
        // A failed lookup must never look like a broken page -- offer the real
        // listing instead, which is where they were going to end up anyway.
        setBody(
          '<p class="fp-preview__err">Couldn\'t load the details just now.</p>' +
            '<p><a class="fp-preview__link" href="/products/' + esc(handle) + '">Open the full product page</a></p>',
          label || ""
        );
      });
  }

  function load(handle) {
    if (CACHE[handle]) return Promise.resolve(CACHE[handle]);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(root + "products/" + encodeURIComponent(handle) + ".js", {
      credentials: "same-origin"
    })
      .then(function (r) {
        if (!r.ok) throw r;
        return r.json();
      })
      .then(function (p) {
        CACHE[handle] = p;
        return p;
      });
  }

  function render(p) {
    var img = p.featured_image || (p.images && p.images[0]);

    // NO PRICE HERE, DELIBERATELY.
    // `/products/<handle>.js` returns the product's standalone catalogue price
    // ($59.40 for the standard battery tray), but bought as an add-on it costs
    // the package price ($53.46 after the 10% set discount). The option row
    // already shows both -- "+$53.46" with the $59.40 struck through -- so
    // repeating the higher number here, styled as *the* price, is three numbers
    // for one item and the most prominent one is the one they will not pay.
    // The option row is the authority on cost; this panel explains the product.
    var html = "";
    html += '<div class="fp-preview__grid">';
    if (img) {
      html += '<div class="fp-preview__media"><img src="' + esc(sized(img)) + '" alt="' + esc(p.title || "") + '" loading="lazy"></div>';
    }
    html += '<div class="fp-preview__meta">';
    html += '<div class="fp-preview__desc">' + (p.description || "<p>No description available.</p>") + "</div>";
    html += '<p><a class="fp-preview__link" href="/products/' + esc(p.handle) + '">See the full product page</a></p>';
    html += "</div></div>";

    setBody(html, p.title || "");
  }

  function build() {
    if (panel) return;
    panel = document.createElement("div");
    panel.className = "fp-preview";
    panel.innerHTML =
      '<div class="fp-preview__backdrop" data-fp-preview-close></div>' +
      '<div class="fp-preview__box" role="dialog" aria-modal="true" aria-labelledby="fpPreviewTitle">' +
      '<button type="button" class="fp-preview__close" aria-label="Close" data-fp-preview-close>&times;</button>' +
      '<h3 class="fp-preview__title" id="fpPreviewTitle"></h3>' +
      '<div class="fp-preview__body"></div>' +
      "</div>";
    document.body.appendChild(panel);

    panel.addEventListener("click", function (e) {
      if (e.target.closest("[data-fp-preview-close]")) close();
    });
  }

  function setBody(html, title) {
    if (!panel) return;
    panel.querySelector(".fp-preview__title").textContent = title;
    panel.querySelector(".fp-preview__body").innerHTML = html;
  }

  function close() {
    if (!panel || !panel.classList.contains("is-open")) return;
    panel.classList.remove("is-open");
    document.documentElement.style.overflow = "";
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---------- helpers ---------- */

  // Shopify CDN URLs accept a size suffix; asking for 480px keeps the panel
  // sharp without pulling a 2000px original.
  function sized(url) {
    if (!url) return url;
    if (url.indexOf("?") > -1) return url + "&width=480";
    return url + "?width=480";
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function cssEscape(s) {
    return String(s).replace(/["\\]/g, "\\$&");
  }

  function parse(el) {
    try {
      var v = JSON.parse(el.textContent);
      return typeof v === "string" ? JSON.parse(v) : v;
    } catch (e) {
      return null;
    }
  }

  function ready(fn) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn);
    else fn();
  }

  function injectStyles() {
    if (document.getElementById("fp-preview-css")) return;
    var s = document.createElement("style");
    s.id = "fp-preview-css";
    s.textContent = [
      ".fp-info{display:inline-flex;align-items:center;gap:5px;padding:3px 11px 3px 9px;",
      "border-radius:999px;border:1px solid rgba(40,60,110,.28);background:#eef1f8;color:#2b3a63;",
      "font-size:12px;font-weight:600;line-height:1.5;cursor:pointer;white-space:nowrap;",
      "vertical-align:middle;margin-left:2px;flex:0 0 auto;transition:background .12s ease}",
      ".fp-info::before{content:\"i\";display:inline-flex;align-items:center;justify-content:center;",
      "width:14px;height:14px;border-radius:50%;background:#2b3a63;color:#eef1f8;",
      "font-size:10px;font-weight:700;font-style:italic;line-height:1;flex:0 0 auto}",
      ".fp-info:hover{background:#dde3f2}",
      ".fp-option__label.fp-has-info{display:flex;align-items:center;gap:8px;flex-wrap:wrap}",
      ".fp-option__label.fp-has-info .fp-info{margin-left:auto}",
      ".fp-info:focus-visible{outline:2px solid currentColor;outline-offset:2px}",
      ".fp-preview{display:none;position:fixed;inset:0;z-index:99999}",
      ".fp-preview.is-open{display:block}",
      ".fp-preview__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.5)}",
      ".fp-preview__box{position:relative;max-width:560px;width:calc(100% - 32px);max-height:80vh;overflow:auto;",
      "margin:8vh auto 0;background:#fff;border-radius:8px;padding:20px 22px;box-shadow:0 18px 50px rgba(0,0,0,.3)}",
      ".fp-preview__close{position:absolute;top:8px;right:10px;border:0;background:none;font-size:26px;",
      "line-height:1;cursor:pointer;opacity:.5;padding:4px}",
      ".fp-preview__close:hover{opacity:1}",
      ".fp-preview__title{margin:0 28px 12px 0;font-size:17px;line-height:1.3}",
      ".fp-preview__grid{display:flex;gap:16px;flex-wrap:wrap}",
      ".fp-preview__media{flex:0 0 160px}",
      ".fp-preview__media img{width:100%;height:auto;border-radius:6px;display:block}",
      ".fp-preview__meta{flex:1 1 260px;min-width:0}",
      ".fp-preview__desc{font-size:14px;line-height:1.5}",
      ".fp-preview__desc img{max-width:100%;height:auto}",
      ".fp-preview__link{text-decoration:underline}",
      ".fp-preview__loading,.fp-preview__err{opacity:.7;font-size:14px}",
      "@media (max-width:600px){.fp-preview__box{margin:4vh auto 0;max-height:88vh}.fp-preview__media{flex:0 0 100%}}"
    ].join("");
    document.head.appendChild(s);
  }
})();
